import { StringOutputParser } from "@langchain/core/output_parsers";
import { PromptTemplate } from "@langchain/core/prompts";
import { parseAiJson } from "./aiJson";
import { getChatModel } from "./llmClient";

async function buildLLM(provider = "openai") {
  return getChatModel(provider);
}

const FIELD_LABELS = {
  title: "Working Title",
  purpose: "Purpose/Goal",
  primaryAudience: "Primary Audience",
  secondaryAudience: "Secondary Audience",
  audienceKnowledgeLevel: "Audience Knowledge Level",
  wordCount: "Target Word Count",
  tone: "Tone & Voice",
  primaryKeywordsRaw: "Primary Keywords",
  secondaryKeywordsRaw: "Secondary Keywords",
  referenceUrlsRaw: "Reference URLs",
  keyMessagesRaw: "Key Messages / Takeaways",
  commonMistakesRaw: "Common Mistakes / Misconceptions / Rules",
  additionalNotes: "Additional Notes",
};

function formatInputsBlock(inputs) {
  return Object.entries(FIELD_LABELS)
    .map(([key, label]) => `- ${label}: ${inputs[key] || "(not provided)"}`)
    .join("\n");
}

function formatEmphasis(agent, inputs) {
  if (!agent.emphasize?.length) return "";
  const lines = agent.emphasize
    .map((key) => `${FIELD_LABELS[key] || key} = ${inputs[key] || "(not provided)"}`)
    .join(", ");
  return `Pay particular attention to: ${lines}`;
}

// Each rubric is the user's own wording, verbatim — not paraphrased.
const EDITOR_AGENTS = [
  {
    key: "readability",
    label: "Readability Agent",
    focus: "Clarity, structure, language, formatting.",
    rubric:
      "Making sure each section is substantiative and is not dominated by bullet points. Adheres to the Audience Knowledge Level input, and the Tone & Voice input.",
    emphasize: ["audienceKnowledgeLevel", "tone"],
  },
  {
    key: "topicAuthority",
    label: "Topic Authority Agent",
    focus: "Depth, accuracy, coverage, credibility.",
    rubric:
      "Important factors include depth of explanation, factual accuracy, coverage of important subtopics, examples and supporting evidence, appropriate terminology, credible sources, answering likely follow-up questions, and demonstrating firsthand or specialized knowledge where appropriate.",
    emphasize: [],
  },
  {
    key: "contentValue",
    label: "Content Value Agent",
    focus: "Usefulness, originality, relevance, actionability.",
    rubric:
      "Look at whether the article answers the reader's question, provides actionable advice, gives concrete examples, offers insights beyond obvious information, solves a problem, matches the intended audience, avoids unnecessary filler, and leaves the reader knowing or being able to do something they couldn't before.",
    emphasize: [],
  },
  {
    key: "seo",
    label: "SEO Agent",
    focus: "Search intent, keywords, optimization, discoverability.",
    rubric:
      "Evaluate things such as search intent, primary and secondary keyword usage, title and headings, topical relevance, semantic terms and related concepts, internal/external links, meta title and description, URL structure, image alt text, content organization, originality, and whether the article adequately answers the query it's targeting.",
    emphasize: [],
  },
];

const reviewFormat = `{
  "issues": [
    {
      "exact_text": "<verbatim snippet copied word-for-word from the article>",
      "reason": "<why this should be improved>",
      "suggested_replacement": "<replacement text the user could paste 1:1>",
      "severity": "low|medium|high"
    }
  ]
}`;

async function runReviewPass(agent, articleHtml, inputs, provider = "openai") {
  try {
    const llm = await buildLLM(provider);

    const promptTemplate = `You are the {agentLabel}, one of a team of specialist editors reviewing an AI-generated article before publication.

Your sole focus: {focus}

Evaluation criteria:
{rubric}

{emphasisLine}

Task:
- Review the article below strictly against your evaluation criteria above. Do not comment on anything outside your area of focus — other specialists are reviewing those concerns separately.
- Return 0-8 specific issues. If the article already satisfies your criteria well, return an empty "issues" array.
- "exact_text" MUST be copied verbatim from the provided HTML so it can be located exactly later. Do not normalize quotes, punctuation, or whitespace. Do not include HTML tags, just the text.
- "suggested_replacement" MUST be a drop-in replacement for "exact_text" (same voice/context), not generic advice.

Background on this article (for context only — do not flag issues outside your focus area based on this):
{inputsBlock}

Article HTML:
{articleHtml}

Return ONLY a single JSON object matching the 'Format' exactly. Do not use backticks or add any commentary.
Format: {format}`;

    const prompt = PromptTemplate.fromTemplate(promptTemplate);
    const chain = prompt.pipe(llm).pipe(new StringOutputParser());

    const promptInputs = {
      agentLabel: agent.label,
      focus: agent.focus,
      rubric: agent.rubric,
      emphasisLine: formatEmphasis(agent, inputs),
      inputsBlock: formatInputsBlock(inputs),
      articleHtml,
      format: reviewFormat,
    };

    console.log(`[${agent.label}] Prompt variables:`, promptInputs);

    const response = await chain.invoke(promptInputs);
    const parsed = parseAiJson(response);

    console.log(`[${agent.label}] Findings:`, parsed);

    return Array.isArray(parsed.issues) ? parsed.issues : [];
  } catch (e) {
    console.error(`[${agent.label}] review pass failed:`, e);
    return [];
  }
}

const integrationFormat = `{
  "revised_html": "<String: the complete revised article as valid HTML>",
  "meta_tags": "<String: the complete <head> block, valid HTML>",
  "changes_summary": "<String: brief summary of what was changed and why>"
}`;

function formatFindings(issues) {
  if (!issues?.length) return "No issues found.";
  return JSON.stringify(issues, null, 2);
}

async function runIntegrationPass(
  articleHtml,
  metaTags,
  findingsByAgent,
  provider = "openai"
) {
  try {
    const llm = await buildLLM(provider);

    const promptTemplate = `You are the final editor on a team that has just reviewed an AI-generated article across four categories: Readability, Topic Authority, Content Value, and SEO. Each specialist has flagged issues in their area. Your job is to produce the final, publication-ready version of the article by applying their feedback.

Task:
- Incorporate the valid suggestions below into the article, resolving any overlapping or conflicting suggestions using your best editorial judgment.
- Preserve everything that wasn't flagged.
- Keep the output valid semantic HTML (no markdown, no stray newlines for spacing).
- Also review and, if needed, update the Meta Tags block below to reflect the SEO team's feedback (title tag, meta description, etc.) — this is the only place meta tags should be revised.
- Briefly summarize what you changed and why in "changes_summary".

Readability Agent findings:
{readabilityFindings}

Topic Authority Agent findings:
{topicAuthorityFindings}

Content Value Agent findings:
{contentValueFindings}

SEO Agent findings:
{seoFindings}

Current Article HTML:
{articleHtml}

Current Meta Tags:
{metaTags}

Return ONLY a single JSON object matching the 'Format' exactly. Do not use backticks or add any commentary.
Format: {format}`;

    const prompt = PromptTemplate.fromTemplate(promptTemplate);
    const chain = prompt.pipe(llm).pipe(new StringOutputParser());

    const promptInputs = {
      readabilityFindings: formatFindings(findingsByAgent.readability),
      topicAuthorityFindings: formatFindings(findingsByAgent.topicAuthority),
      contentValueFindings: formatFindings(findingsByAgent.contentValue),
      seoFindings: formatFindings(findingsByAgent.seo),
      articleHtml,
      metaTags,
      format: integrationFormat,
    };

    console.log("[Integration Pass] Prompt variables:", promptInputs);

    const response = await chain.invoke(promptInputs);
    const parsed = parseAiJson(response);

    console.log("[Integration Pass] Changes summary:", parsed.changes_summary);

    return {
      articleHtml: parsed.revised_html || articleHtml,
      metaTags: parsed.meta_tags || metaTags,
    };
  } catch (e) {
    console.error("[Integration Pass] failed, keeping article unchanged:", e);
    return { articleHtml, metaTags };
  }
}

// Runs the 4 editor agents concurrently as reviewers, then a single
// integration pass applies their combined findings to produce the final
// article. Returns { articleHtml, metaTags }.
export async function runEditorReview(
  articleHtml,
  metaTags,
  inputs,
  onProgress,
  provider = "openai"
) {
  onProgress?.("Reviewing article quality...");

  const [readability, topicAuthority, contentValue, seo] = await Promise.all(
    EDITOR_AGENTS.map((agent) =>
      runReviewPass(agent, articleHtml, inputs, provider)
    )
  );

  const findingsByAgent = { readability, topicAuthority, contentValue, seo };

  onProgress?.("Finalizing revisions...");

  return runIntegrationPass(articleHtml, metaTags, findingsByAgent, provider);
}
